import type {CustomerScanContext} from "../scan-model/CustomerScanContext.tsx";
import type {
    SingleTaskBatchScanResult
} from "../../../analysis-process/3-perform-scan/scan-model/GroupedTaskBatchesScanResult.tsx";
import type {AnalysisOptionTaskHistory} from "../../../analysis-process/2-option-selection/AnalysisOptions.ts";
import type {MetlApiBase} from "../../../metl-api/MetlApiBase.ts";
import type {ProjectId} from "../../../metl-models/id/ProjectId.tsx";
import type {WorkloadJobHolder} from "../../3-workload-analysis/model/WorkloadJobHolder.ts";
import {runPromises} from "../../../util/promises/PromiseRunner.tsx";
import {calculateSingleBatchAnalysis} from "../../../analysis-process/3-perform-scan/scan-operations/task/SingleBatchCalculator.tsx";
import type {TransformationJob} from "../../../metl-models/jobs/Transformation.ts";
import type {OrchestrationJob} from "../../../metl-models/jobs/Orchestration.ts";

function generateTimeSplits(options: AnalysisOptionTaskHistory): [start: Date, end: Date][] {
    const {durationToQueryInDays, taskQueryDurationHours} = options
    const now = new Date()
    const absoluteStart = new Date(now.getTime() - durationToQueryInDays * 24 * 60 * 60 * 1000)
    const numberOfRegions = Math.ceil((durationToQueryInDays * 24) / taskQueryDurationHours)
    const splits: [start: Date, end: Date][] = []
    for(let i = 0; i < numberOfRegions; i++) {
        const start = absoluteStart.getTime() + (i * taskQueryDurationHours * 60 * 60 * 1000)
        const end = start + (taskQueryDurationHours * 60 * 60 * 1000) - 1
        splits.push([
            new Date(start),
            new Date(end)
        ])
    }
    return splits
}

export async function scanWorkloadTaskHistory(
    ctx: CustomerScanContext,
    api: MetlApiBase,
    projectId: ProjectId,
    jobs: WorkloadJobHolder[]
): Promise<SingleTaskBatchScanResult[]> {
    const {logError, startAction, options} = ctx
    const splits = generateTimeSplits(options.analyseTaskHistory)
    const {reportOne, complete} = startAction(`Scanning task history of project ${projectId.groupName}/${projectId.projectName}`, splits.length)
    const taskApi = api.groups.get(projectId.groupName).projects.get(projectId.projectName).taskHistory()
    
    // Build version job map from WorkloadJobHolder
    const versionJobMap: Record<string, Record<string, TransformationJob | OrchestrationJob>> = {}
    for(const jobHolder of jobs) {
        // Extract version from the job holder (we'll need to track this)
        // For now, we'll use a simplified approach - you may need to enhance this
        const versionName = "default" // This should come from the scan context
        const versionJobs = versionJobMap[versionName] ?? {}
        versionJobs[jobHolder.name] = jobHolder.job
        versionJobMap[versionName] = versionJobs
    }
    
    const runnerOpts = options.analyseTaskHistory.promiseRunner
    const taskGroups = await runPromises(runnerOpts, splits, async ([start, end]) => {
        try {
            const batches = await taskApi.queryBetween(start, end)
            return batches.map(batch => calculateSingleBatchAnalysis(versionJobMap[batch.versionName], batch))
        } catch(e) {
            console.error("Failed to scan task history split", projectId, [start, end], e)
            logError(`Failed to scan task history split ${projectId.groupName}/${projectId.projectName} [${start.toISOString()} - ${end.toISOString()}]`, e)
            return []
        } finally {
            reportOne()
        }
    })
    complete()
    
    return taskGroups.flat()
}
